/* =====================================================================
   VORTO — Motor de idioma (React)
   ---------------------------------------------------------------------
   Provider + hook useI18n. Escolhe o idioma inicial (escolha salva >
   navegador), troca na hora via contexto e mantém <title>/<meta>/lang
   do documento em sincronia. Não precisa mexer aqui.
   ===================================================================== */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { I18N, type Lang } from "./dictionaries";

const STORAGE = "vorto-lang";

type I18nContextValue = {
  lang: Lang;
  /** Tradução de uma chave no idioma atual (fallback: PT). */
  t: (key: string) => string;
  setLang: (lang: Lang) => void;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function idiomaInicial(): Lang {
  try {
    const salvo = localStorage.getItem(STORAGE);
    if (salvo === "pt" || salvo === "en" || salvo === "es") return salvo;
  } catch {
    /* storage bloqueado — ignora */
  }
  const nav = (navigator.language || "pt").toLowerCase();
  if (nav.startsWith("pt")) return "pt";
  if (nav.startsWith("es")) return "es";
  return "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(idiomaInicial);

  const setLang = useCallback((novo: Lang) => {
    setLangState(novo);
    try {
      localStorage.setItem(STORAGE, novo);
    } catch {
      /* ignora */
    }
  }, []);

  // título, descrição e atributo lang da página acompanham o idioma
  useEffect(() => {
    const dict = I18N[lang];
    if (dict["meta.title"]) document.title = dict["meta.title"];
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", dict["meta.desc"]);
    document.documentElement.lang = lang === "pt" ? "pt-BR" : lang;
  }, [lang]);

  const t = useCallback(
    (key: string) => I18N[lang][key] ?? I18N.pt[key] ?? "",
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, t, setLang }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n precisa estar dentro de <I18nProvider>");
  return ctx;
}
